/* eslint-disable no-redeclare */
import {
  EntryStatusEnum,
  type Event,
  EventTypeEnum,
  GenderEnum,
  type Participant,
  ParticipantTypeEnum,
  SexEnum,
} from '@clubspark-node/clubspark-tennis-types';
import dayjs from 'dayjs';
import { type TFunction } from 'i18next';
import { participantRoles, participantTypes } from 'tods-competition-factory';

import { getCorrectAgeCategory } from 'components/dialogs/scorecardTemplate/utils';
import { playerStatusSort } from 'constants/playerStatus';
import { Gender } from 'typedefs/event';
import { type MatchUpInterface } from 'typedefs/store/scheduleTypes';
import { type EventInterface, type ParticipantInterface } from 'typedefs/store/tmxTypes';

const { COMPETITOR } = participantRoles;
const { PAIR, TEAM, INDIVIDUAL } = participantTypes;

export const formatPlayerName = (givenName?: string, familyName?: string) => {
  if (givenName && familyName) {
    return familyName + ', ' + givenName;
  } else {
    return '';
  }
};

export function participantName(participant) {
  if (participant?.person) {
    return formatPlayerName(participant.person?.standardGivenName, participant.person?.standardFamilyName);
  } else {
    return participant?.participantName ?? participant?.name ?? '';
  }
}

export const getParticipantName = (
  participant: ParticipantInterface,
  isDoubles: boolean,
  t: TFunction,
  isBye?: boolean,
  isQualifier?: boolean,
) => {
  if (isBye) {
    return t('bye');
  } else if (isQualifier) {
    return t('qualifier');
  } else if (!participant) {
    return '';
  } else {
    if (isDoubles) return getDoublesName(participant);
    return participant?.person
      ? formatPlayerName(participant.person?.standardGivenName, participant.person?.standardFamilyName)
      : participant?.participantName;
  }
};

export const getDoublesName = (participant: ParticipantInterface) => {
  const individualParticipants = participant?.individualParticipants || [];
  const participantOne = individualParticipants[0];
  const participantTwo = individualParticipants[1];

  if (!participantOne || !participantTwo) return '';

  const participantOneString = `${
    participantOne?.person?.standardFamilyName
  }, ${participantOne?.person?.standardGivenName.substring(0, 1)}`;
  const participantTwoString = `${
    participantTwo?.person?.standardFamilyName
  }, ${participantTwo?.person?.standardGivenName.substring(0, 1)}`;

  return `${participantOneString || ''} / ${participantTwoString || ''}`;
};

export const getPersonNameFromParticipant = (participant: ParticipantInterface) =>
  participant.person
    ? `${participant?.person?.standardFamilyName}, ${participant?.person?.standardGivenName}`
    : participant.participantName;

export const getDoublesParticipantNames = (participant) => {
  const individualParticipants = participant?.individualParticipants || [];
  const participantOne = individualParticipants[0];
  const participantTwo = individualParticipants[1];

  if (!participantOne || !participantTwo) return '';

  const participantOneString = getPersonNameFromParticipant(participantOne);
  const participantTwoString = getPersonNameFromParticipant(participantTwo);
  return [participantOneString || '', participantTwoString || ''];
};

export const getAddress = (participant: ParticipantInterface) => participant?.person?.addresses?.[0];
export const getDoublesAddress = (participant) => {
  const individualParticipants = participant?.individualParticipants || [];
  const participantOne = individualParticipants[0];
  const participantTwo = individualParticipants[1];
  const { city: cityOne, state: stateOne } = participantOne?.person?.addresses?.[0] || {};
  const cityStateOne = [cityOne, stateOne].filter((f) => f).join(', ') || '';
  const { city: cityTwo, state: stateTwo } = participantTwo?.person?.addresses?.[0] || {};
  const cityStateTwo = [cityTwo, stateTwo].filter((f) => f).join(', ') || '';

  if (!cityStateOne || !cityStateTwo) return [];

  return [cityStateOne || '', cityStateTwo || ''];
};

export const getDoublesSectionAndDistrict = (participant, type: string) => {
  const individualParticipants = participant?.individualParticipants || [];
  const participantOne = individualParticipants[0];
  const participantTwo = individualParticipants[1];

  if (type === 'ustaDistrict') {
    const districtOne = participantOne?.person?.extensions?.find((e) => e.name === type)?.value?.name;
    const districtTwo = participantTwo?.person?.extensions?.find((e) => e.name === type)?.value?.name;
    if (!districtOne || !districtTwo) return [];
    return [districtOne ?? '', districtTwo ?? ''];
  } else if (type === 'ustaSection') {
    const sectionOne = participantOne?.person?.extensions?.find((e) => e.name === type)?.value?.name;
    const sectionTwo = participantTwo?.person?.extensions?.find((e) => e.name === type)?.value?.name;
    if (!sectionOne || !sectionTwo) return [];
    return [sectionOne || '', sectionTwo || ''];
  } else return [];
};

export const getDoublesOrganisationIds = (participant: ParticipantInterface): string[] => {
  const individualParticipants = participant?.individualParticipants || [];
  const participantOne = individualParticipants[0];
  const participantTwo = individualParticipants[1];

  const otherIdsOne = participantOne?.person?.personOtherIds;
  const otherIdsTwo = participantTwo?.person?.personOtherIds;

  if (otherIdsOne && otherIdsTwo) {
    return [otherIdsOne?.[0]?.personId ?? '', otherIdsTwo?.[0]?.personId ?? ''];
  } else {
    return [];
  }
};

export const getDoublesIds = (participant: ParticipantInterface) => {
  const individualParticipants = participant?.individualParticipants || [];
  const participantOne = individualParticipants[0];
  const participantTwo = individualParticipants[1];

  const idOne = participantOne?.person.personId;
  const idTwo = participantTwo?.person.personId;

  if (!idOne || !idTwo) return [];

  return [idOne || '', idTwo || ''];
};

export function participantsMapper(sides: { participant: ParticipantInterface }[]) {
  return (
    sides
      ?.flatMap((side) => {
        if (!side) {
          return null;
        }

        const isDouble = side?.participant?.individualParticipants?.length > 0;
        if (isDouble) {
          return side.participant.individualParticipants.map((participant) => ({
            ...participant,
            participantName: formatPlayerName(
              participant?.person?.standardGivenName,
              participant?.person?.standardFamilyName,
            ),
          }));
        }
        return {
          ...side.participant,
          participantName: formatPlayerName(
            side.participant?.person?.standardGivenName,
            side.participant?.person?.standardFamilyName,
          ),
        };
      })
      ?.filter((participant) => participant?.participantId) || []
  );
}

export const getMainDrawEvents = (team) => {
  return (
    team?.events?.filter(
      (event) =>
        event?.entryStatus === 'DIRECT_ACCEPTANCE' ||
        (event?.entryStatus === 'ALTERNATE' && event?.drawIds?.length > 0),
    ) ?? []
  );
};

export function getParticipantNameWithSeedValue(participantName: string, seedValue: number): string;
export function getParticipantNameWithSeedValue(participantName: string[], seedValue: number): string[];
export function getParticipantNameWithSeedValue(
  participantName: string | string[],
  seedValue: number,
): string | string[] {
  if (!seedValue) {
    return participantName;
  }

  const format = (name) => `${name} [${seedValue}]`;
  return Array.isArray(participantName) ? participantName.map(format) : format(participantName);
}

export const getTeamsFromParticipants = (participants: ParticipantInterface[]): ParticipantInterface[] =>
  participants.filter((p) => p.participantRole === COMPETITOR && p.participantType === TEAM);

export const getPlayersFromParticipants = (participants: ParticipantInterface[]) =>
  participants.filter((p) => p.participantRole === COMPETITOR && p.participantType === INDIVIDUAL);

export type ParticipantInterfaceWithTeams = ParticipantInterface & {
  teams?: ParticipantInterface[];
  individualParticipants?: ParticipantInterface[];
};

export const getParticipantsDisplayName = (rowItem) =>
  (rowItem?.participantType === ParticipantTypeEnum.TEAM || !rowItem?.individualParticipants?.length
    ? [rowItem]
    : rowItem.individualParticipants
  )
    .map((participant) => participant.participantName)
    .join(' & ');

export const getParticipantsOrganisationIds = (participants: ParticipantInterface[], isDoubles: boolean) => {
  return participants?.map((p) => {
    if (isDoubles) {
      const doublesIds =
        p?.individualParticipantIds?.length > 0
          ? p?.individualParticipants?.map((participant) => {
              return {
                ...(participant?.person?.personOtherIds?.[0] ?? {}),
                doublesId: p?.participantId,
              };
            }) || []
          : [(p?.person?.personOtherIds && p?.person?.personOtherIds?.[0]) || ''];
      return [...doublesIds];
    } else {
      return (p?.person?.personOtherIds && p?.person?.personOtherIds?.[0]) || '';
    }
  });
};

export const getParticipantsNationalIds = (participants: ParticipantInterface[], isDoubles: boolean): string[] => {
  return participants
    ?.map((p) => {
      if (isDoubles) {
        const doublesIds =
          p?.individualParticipantIds?.length > 0
            ? p?.individualParticipants?.map((participant) => {
                return (
                  (participant?.person?.personOtherIds && participant?.person?.personOtherIds?.[0]?.personId) || ''
                );
              }) || []
            : [(p?.person?.personOtherIds && p?.person?.personOtherIds?.[0]?.personId) || ''];
        return [...doublesIds];
      } else {
        return (p?.person?.personOtherIds && p?.person?.personOtherIds?.[0]?.personId) || '';
      }
    })
    ?.flat();
};

export const getParticipantsPersonIds = (participants: ParticipantInterface[], isDoubles: boolean): string[] => {
  return participants
    ?.map((p) => {
      if (isDoubles && p?.individualParticipants?.length) {
        return p.individualParticipants.map((participant) => participant?.person?.personId);
      }
      return p?.person?.personId;
    })
    .flat()
    .filter(Boolean);
};

export const hydrateIndividualParticipants =
  (participants: ParticipantInterface[], event: Event) => (participant: ParticipantInterface) => {
    return (event.eventType !== EventTypeEnum.DOUBLES && event.eventType !== EventTypeEnum.TEAM) ||
      participant?.individualParticipantIds?.length === 0
      ? participant
      : {
          ...participant,
          individualParticipants: participants?.filter((tournamentParticipant) =>
            participant?.individualParticipantIds?.includes(tournamentParticipant.participantId),
          ),
        };
  };

export const entryRowSort = (a: EntryStatusEnum, b: EntryStatusEnum) => playerStatusSort[a] - playerStatusSort[b];

export const getMatchUpParticipants = (selectedMatchUp: MatchUpInterface) => {
  return selectedMatchUp?.sides?.map((side) => {
    const isDoubles = side?.participant?.participantType === PAIR;

    const participantName =
      (side?.participant &&
        (isDoubles
          ? [...getDoublesParticipantNames(side?.participant)]?.join(' / ')
          : getPersonNameFromParticipant(side?.participant))) ||
      undefined;

    return {
      participantId: side?.participant?.participantId,
      participantName: participantName,
      qualifier: side?.qualifier && !side?.participant,
    };
  });
};

export const getParticipantMapperFor =
  (participants: ParticipantInterface[]) =>
  ({ participantId }: { participantId: string }) =>
    participants.find((participant) => participant.participantId === participantId);

export const participantTypeIndividualFilter = (participant: ParticipantInterface) =>
  participant?.participantType === ParticipantTypeEnum.INDIVIDUAL;

export const getParticipants = (participants) => {
  return participants?.map((participant) => {
    return {
      participantId: participant?.participantId,
      participantName: participant?.person
        ? formatPlayerName(participant?.person?.standardGivenName, participant?.person?.standardFamilyName)
        : participant?.participantName,
      participantType: participant?.participantType,
      individualParticipantIds: participant?.individualParticipantIds,
    };
  });
};

export const getFullDoublesName = (doublesParticipant: ParticipantInterface) => {
  const individualParticipants = doublesParticipant?.individualParticipants || [];
  const participantOne = individualParticipants[0];
  const participantTwo = individualParticipants[1];

  if (!participantOne || !participantTwo) return [];

  const participantOneString = formatPlayerName(
    participantOne?.person?.standardFamilyName,
    participantOne?.person?.standardGivenName,
  );
  const participantTwoString = formatPlayerName(
    participantTwo?.person?.standardFamilyName,
    participantTwo?.person?.standardGivenName,
  );
  return [
    { id: participantOne.participantId, name: participantOneString },
    { id: participantTwo.participantId, name: participantTwoString },
  ];
};

export const getTeamPlayerNames = (
  { individualParticipants }: { individualParticipants?: ParticipantInterface[] },
  isTeam: boolean,
) => {
  const teamPlayerNames = individualParticipants?.map((p) => {
    return { id: p?.personId, name: formatPlayerName(p?.person?.standardFamilyName, p?.person?.standardGivenName) };
  });

  return isTeam ? teamPlayerNames : undefined;
};

export const filterParticipants = (
  participants: Participant[],
  filterFunctions: ((participant: Participant) => boolean)[],
): Participant[] => {
  return participants.filter((participant) => filterFunctions.every((filterFunction) => filterFunction(participant)));
};

export const filterParticipantByAge = (
  participant: Participant,
  isAdultCategory: boolean,
  isJuniorCategory: boolean,
  ageCategoryCode: string,
  enforceScorecardAge?: boolean,
) => {
  const age = getCorrectAgeCategory(ageCategoryCode)?.age;
  if (!isNaN(age) && enforceScorecardAge) {
    const participantAge = dayjs().diff(participant?.person?.birthDate, 'year');
    if (isAdultCategory) return participantAge >= age;
    if (isJuniorCategory) return participantAge <= age;
  }

  return true;
};

export const filterParticipantByGender = (
  participant: Participant,
  gender: GenderEnum | Gender.COED,
  selectedGender?: GenderEnum,
  isSubstitution?: boolean,
  enforceScorecardGender?: boolean,
) => {
  if (!enforceScorecardGender) return true;
  if (gender === GenderEnum.MIXED && selectedGender) {
    if (isSubstitution) return getGenderFromSex(participant?.person?.sex) === selectedGender;
    return getGenderFromSex(participant?.person?.sex) !== selectedGender;
  } else if (gender === GenderEnum.MIXED && !selectedGender) {
    return true;
  }
  if (gender === Gender.COED) return true;
  return getGenderFromSex(participant?.person?.sex) === gender;
};

export const getGenderFromSex = (sex: SexEnum) => {
  switch (sex) {
    case SexEnum.MALE:
      return GenderEnum.MALE;
    case SexEnum.FEMALE:
      return GenderEnum.FEMALE;
    default:
      return GenderEnum.ANY;
  }
};

export const isParticipantSuspended = (participant: ParticipantInterface) => {
  const activeSuspension = participant?.extensions?.find((extension) => extension?.name === 'activeSuspension')?.value;
  return { isSuspended: !!activeSuspension };
};

export const isParticipantRequestedWithdrawal = (participant: ParticipantInterface, events: EventInterface[]) => {
  const withdrawalRequestsExtension = participant?.extensions?.find(
    (extension) => extension?.name === 'eventWithdrawalRequests',
  )?.value;
  if (withdrawalRequestsExtension?.length) {
    const eventIds = withdrawalRequestsExtension?.map((withdrawalRequest) => withdrawalRequest.eventId);
    const requestedWithdrawnEventIds = events
      ?.filter(
        (event) =>
          eventIds?.includes(event?.eventId) &&
          event?.entries?.find((entry) => entry?.participantId === participant?.participantId)?.entryStatus !==
            EntryStatusEnum.WITHDRAWN,
      )
      ?.map((event) => event?.eventId);

    const withdrawnEventIds = events
      ?.filter(
        (event) =>
          eventIds?.includes(event?.eventId) &&
          event?.entries?.find((entry) => entry?.participantId === participant?.participantId)?.entryStatus ===
            EntryStatusEnum.WITHDRAWN,
      )
      ?.map((event) => event?.eventId);

    return {
      isRequestedWithdrawal: requestedWithdrawnEventIds?.some((eventId) => !withdrawnEventIds?.includes(eventId)),
      requestedWithdrawnEventIds,
    };
  }

  return { isRequestedWithdrawal: false, requestedWithdrawnEventIds: [] };
};
